import { onDocumentReady } from '../utils/events';

onDocumentReady(() => {
  const accordions = document.querySelectorAll('.info-accordion');
  const accordionItems = document.querySelectorAll('.info-accordion__item');
  const serviceItems = document.querySelectorAll('.services-details__item');
  const descriptionTransitionDuration = 300;

  const setTruncateHeights = (element, lineClampedClass, heightPrefix) => {
    if (!element.textContent.trim()) return false;

    element.dataset.isTruncated = 'true';
    element.classList.add(lineClampedClass);

    const collapsedHeight = element.getBoundingClientRect().height;

    element.classList.remove(lineClampedClass);

    const expandedHeight = element.scrollHeight;

    if (expandedHeight <= collapsedHeight + 1) {
      delete element.dataset.isTruncated;
      return false;
    }

    element.style.setProperty(
      `--${heightPrefix}-height`,
      `${collapsedHeight}px`
    );
    element.style.setProperty(
      `--${heightPrefix}-height-expanded`,
      `${expandedHeight}px`
    );
    element.classList.add(lineClampedClass);

    return true;
  };

  const openTruncatedElement = (
    element,
    lineClampedClass,
    expandedClass,
    heightPrefix
  ) => {
    if (!element || element.dataset.isTruncated !== 'true') return;

    element.classList.remove(lineClampedClass);

    window.requestAnimationFrame(() => {
      element.style.setProperty(
        `--${heightPrefix}-height-expanded`,
        `${element.scrollHeight}px`
      );
      element.classList.add(expandedClass);
    });
  };

  const closeTruncatedElement = (
    element,
    lineClampedClass,
    expandedClass,
    shouldKeepOpen
  ) => {
    if (!element || element.dataset.isTruncated !== 'true') return;

    element.classList.remove(expandedClass);

    window.setTimeout(() => {
      if (shouldKeepOpen()) return;

      element.classList.add(lineClampedClass);
    }, descriptionTransitionDuration);
  };

  const openItemDescription = (item) => {
    openTruncatedElement(
      item.querySelector('.info-accordion__item-description'),
      'info-accordion__item-description--line-clamped',
      'info-accordion__item-description--expanded',
      'description'
    );
  };

  const closeItemDescription = (item) => {
    closeTruncatedElement(
      item.querySelector('.info-accordion__item-description'),
      'info-accordion__item-description--line-clamped',
      'info-accordion__item-description--expanded',
      () => item.classList.contains('is-active')
    );
  };

  const setBodyHidden = (body, isHidden) => {
    if (!body) return;

    body.setAttribute('aria-hidden', String(isHidden));

    if (isHidden) {
      body.setAttribute('inert', '');
      return;
    }

    body.removeAttribute('inert');
  };

  const openItem = (item) => {
    const head = item.querySelector('.info-accordion__item-head');
    const body = item.querySelector('.info-accordion__item-body');

    item.classList.add('is-active');
    head?.setAttribute('aria-expanded', 'true');
    setBodyHidden(body, false);
    openItemDescription(item);
  };

  const closeItem = (item) => {
    const head = item.querySelector('.info-accordion__item-head');
    const body = item.querySelector('.info-accordion__item-body');

    item.classList.remove('is-active');
    head?.setAttribute('aria-expanded', 'false');
    setBodyHidden(body, true);
    closeItemDescription(item);
  };

  const updateOpenAllButton = (accordion) => {
    const openAllButton = accordion?.querySelector('.info-accordion__open-all');
    const items = accordion?.querySelectorAll('.info-accordion__item');

    if (!openAllButton || !items?.length) return;

    const allItemsOpen = Array.from(items).every((item) =>
      item.classList.contains('is-active')
    );

    openAllButton.classList.toggle('is-active', allItemsOpen);
    openAllButton.setAttribute('aria-pressed', String(allItemsOpen));
  };

  const updateIntroDescription = (serviceItem) => {
    if (!serviceItem) return;

    const introDescription = serviceItem.querySelector(
      '.services-details__intro-description'
    );
    const hasActiveAccordion = Boolean(
      serviceItem.querySelector('.info-accordion__item.is-active')
    );

    if (hasActiveAccordion) {
      openTruncatedElement(
        introDescription,
        'services-details__intro-description--line-clamped',
        'services-details__intro-description--expanded',
        'intro-description'
      );
      return;
    }

    closeTruncatedElement(
      introDescription,
      'services-details__intro-description--line-clamped',
      'services-details__intro-description--expanded',
      () =>
        Boolean(serviceItem.querySelector('.info-accordion__item.is-active'))
    );
  };

  serviceItems.forEach((serviceItem) => {
    const introDescription = serviceItem.querySelector(
      '.services-details__intro-description'
    );

    if (!introDescription) return;

    setTruncateHeights(
      introDescription,
      'services-details__intro-description--line-clamped',
      'intro-description'
    );
  });

  accordionItems.forEach((item) => {
    const head = item.querySelector('.info-accordion__item-head');
    const body = item.querySelector('.info-accordion__item-body');
    const description = item.querySelector('.info-accordion__item-description');

    if (!head || !body) return;

    if (description) {
      setTruncateHeights(
        description,
        'info-accordion__item-description--line-clamped',
        'description'
      );
    }

    head.setAttribute('role', 'button');
    head.setAttribute('tabindex', '0');
    head.setAttribute('aria-expanded', 'false');
    setBodyHidden(body, true);

    const toggleItem = () => {
      const isActive = !item.classList.contains('is-active');

      if (isActive) {
        openItem(item);
        updateIntroDescription(item.closest('.services-details__item'));
        updateOpenAllButton(item.closest('.info-accordion'));
        return;
      }

      closeItem(item);
      updateIntroDescription(item.closest('.services-details__item'));
      updateOpenAllButton(item.closest('.info-accordion'));
    };

    head.addEventListener('click', toggleItem);
    head.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      toggleItem();
    });
  });

  accordions.forEach((accordion) => {
    const openAllButton = accordion.querySelector('.info-accordion__open-all');
    const items = accordion.querySelectorAll('.info-accordion__item');
    const serviceItem = accordion.closest('.services-details__item');

    if (!openAllButton || !items.length) return;

    openAllButton.setAttribute('type', 'button');
    openAllButton.setAttribute('aria-pressed', 'false');

    openAllButton.addEventListener('click', () => {
      const shouldOpen = Array.from(items).some(
        (item) => !item.classList.contains('is-active')
      );

      items.forEach((item) => {
        if (shouldOpen) {
          openItem(item);
          return;
        }

        closeItem(item);
      });

      openAllButton.classList.toggle('is-active', shouldOpen);
      openAllButton.setAttribute('aria-pressed', String(shouldOpen));
      updateIntroDescription(serviceItem);
    });
  });
});
